import React from 'react';
import { motion } from 'framer-motion';
import {
    BookOpenIcon,
    GlobeAltIcon,
    VideoCameraIcon,
    UserGroupIcon,
    BriefcaseIcon,
    ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/solid';

// --- Data for the Resources Page (remains the same) ---
const resourceCategories = [
    {
        name: 'Essential Books',
        icon: BookOpenIcon,
        items: [
            { title: 'Agile Testing: A Practical Guide for Testers and Agile Teams', description: 'A must-read by Lisa Crispin and Janet Gregory that defines the role of a modern tester.', link: 'https://www.amazon.com/Agile-Testing-Practical-Guide-Testers/dp/0321534468' },
            { title: 'The Art of Software Testing, 3rd Edition', description: 'A classic book that covers foundational testing techniques and mindsets.', link: 'https://www.amazon.com/Art-Software-Testing-Glenford-Myers/dp/1118031962' },
            { title: 'Explore It!: Reduce Risk and Increase Confidence with Exploratory Testing', description: 'Elisabeth Hendrickson\'s guide to mastering the valuable skill of exploratory testing.', link: 'https://www.amazon.com/Explore-Increase-Confidence-Exploratory-Testing/dp/1937785025' },
        ],
    },
    {
        name: 'Top Blogs & Websites',
        icon: GlobeAltIcon,
        items: [
            { title: 'Ministry of Testing', description: 'A huge global community with articles, courses, forums, and events for software testers.', link: 'https://www.ministryoftesting.com/' },
            { title: 'Joe Colantonio - Test Guild', description: 'A fantastic resource for news, podcasts, and tutorials on test automation.', link: 'https://testguild.com/' },
            { title: 'Guru99', description: 'Provides a vast library of free tutorials on software testing and other tech topics.', link: 'https://www.guru99.com/software-testing.html' },
        ],
    },
    {
        name: 'YouTube Channels',
        icon: VideoCameraIcon,
        items: [
            { title: 'Rahul Shetty Academy', description: 'In-depth video courses on Selenium, API testing, Cypress, and more automation tools.', link: 'https://www.youtube.com/@rahulshettyacademy' },
            { title: 'Test Automation University', description: 'Free courses taught by industry experts on a wide range of testing topics.', link: 'https://www.youtube.com/@TestAutomationU' },
            { title: 'The Test Lead', description: 'A great channel for insights into QA leadership, career growth, and testing strategy.', link: 'https://www.youtube.com/@TheTestLead' },
        ]
    },
    {
        name: 'Online Communities',
        icon: UserGroupIcon,
        items: [
            { title: 'r/QualityAssurance on Reddit', description: 'An active community for asking questions, sharing experiences, and discussing trends in QA.', link: 'https://www.reddit.com/r/QualityAssurance/' },
            { title: 'The Club - Ministry of Testing', description: 'A dedicated online forum for software testers to connect and learn from each other.', link: 'https://club.ministryoftesting.com/' }
        ]
    },
    {
        name: 'Job Boards',
        icon: BriefcaseIcon,
        items: [
            { title: 'LinkedIn Jobs', description: 'The #1 professional network for finding QA and SDET roles across all experience levels.', link: 'https://www.linkedin.com/jobs/' },
            { title: 'Indeed / Glassdoor', description: 'Major job aggregators with millions of listings for tech positions.', link: 'https://www.indeed.com/' }
        ]
    }
];

// --- Animation Variants ---
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, when: "beforeChildren", staggerChildren: 0.1 } }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const cardHover = {
    y: -5,
    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
    transition: { type: "spring", stiffness: 300 }
};


const ResourcesPage = () => {
    return (
        <div className="space-y-24">
            {/* Page Header */}
            <motion.div
                className="text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                    QA Learning Resources
                </h1>
                <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-600">
                    A curated list of books, blogs, communities, and tools to accelerate your growth as a QA professional.
                </p>
            </motion.div>

            {/* Resource Sections */}
            <div className="space-y-16">
                {resourceCategories.map((category) => (
                    <motion.section
                        key={category.name}
                        variants={sectionVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        <motion.div variants={itemVariants} className="flex items-center border-b border-gray-200 pb-4 mb-8">
                            <category.icon className="h-8 w-8 text-indigo-600 mr-3" />
                            <h2 className="text-3xl font-bold text-gray-900">{category.name}</h2>
                        </motion.div>
                        <motion.ul variants={sectionVariants} className="space-y-6">
                            {category.items.map((item) => (
                                <motion.li
                                    key={item.title}
                                    className="bg-white p-6 rounded-xl shadow-md border border-gray-100"
                                    variants={itemVariants}
                                    whileHover={cardHover}
                                >
                                    <div className="flex flex-col sm:flex-row justify-between sm:items-center">
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-800">{item.title}</h3>
                                            <p className="mt-1 text-gray-600">{item.description}</p>
                                        </div>
                                        <motion.a
                                            href={item.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-4 sm:mt-0 sm:ml-6 inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            Visit
                                            <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
                                        </motion.a>
                                    </div>
                                </motion.li>
                            ))}
                        </motion.ul>
                    </motion.section>
                ))}
            </div>
        </div>
    );
};

export default ResourcesPage;